import { useState, useEffect, useContext, forwardRef } from "react";
import { Link, useLocation } from "react-router-dom";
import NumberFormat from "react-number-format";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiAvatar from "components/SuiAvatar";
import SuiBadge from "components/SuiBadge";
import ConversationList from "components/UpStyled/Conversations";
import ReadMore from "components/UpStyled/ReadMore";
import Tooltip from "@mui/material/Tooltip";

import MarkChatReadIcon from "@mui/icons-material/MarkChatRead";
import toast from "react-hot-toast";
import moment from "moment";
import { capitalCase } from "capital-case";
import { MANUFACTURER_SERVICES } from "utils/data";
import { filterParser } from "utils/helper";
import { AccountContext } from "context/account";

import FilePreviewCard from "components/UpStyled/FilePreviewCard";
import api from "services/api";

const ProjectInfo = forwardRef(({ project, showResponseStatus = false }, ref) => {
  const [othersFiles, setOthersFiles] = useState(project?.metadata?.othersFiles || []);
  const [maList, setMaList] = useState([]);

  const user = useContext(AccountContext);

  useEffect(() => {
    setOthersFiles(project?.metadata?.othersFiles || []);
    if (user.id && user.account_type == "de") {
      //only designer can see the audience list
      const audiences = project?.audiences || [];
      retrieveManufacturerList(audiences);
    }
  }, [project.id, user.id]);

  //Ideation, Sourcing, Technical Design, Sampling, Production
  const renderServiceItems = MANUFACTURER_SERVICES.map((s, index) => {
    let services_required = project?.quote?.services_required || {};

    let badges =
      services_required[s.value] && services_required[s.value]["tags"]
        ? services_required[s.value]["tags"]
        : [];
    return services_required[s.value] ? (
      <SuiBox key={index} display="flex" pt={1} className="flex gap-1 items-start">
        <SuiTypography
          variant="body1"
          fontWeight="bold"
          textTransform="capitalize"
          color="text"
          sx={{ minWidth: 130 }}
        >
          {s.label}:
        </SuiTypography>
        <SuiBox>
          <div className="flex flex-wrap gap-2 items-start">
            {badges && badges.length > 0 ? (
              badges.map((item) => (
                <SuiBadge
                  key={item}
                  container
                  variant="contained"
                  textTransform="none"
                  color="secondary"
                  size="sm"
                  badgeContent={capitalCase(item, { stripRegexp: /[^A-Z0-9\'\&\/\-_]/gi })}
                />
              ))
            ) : (
              <SuiBadge
                container
                variant="contained"
                textTransform="none"
                color="secondary"
                size="sm"
                badgeContent={capitalCase(s.label, { stripRegexp: /[^A-Z0-9\'\&\/\-_]/gi })}
              />
            )}
          </div>
        </SuiBox>
      </SuiBox>
    ) : null;
  });

  const budgetMin = project?.quote?.budget_min;
  const budgetMax = project?.quote?.budget_max;

  const retrieveManufacturerList = async (audiences) => {
    if (audiences && audiences.length > 0) {
      let payload = { sort: ["account_status:asc"], offset: 0, limit: 100 };
      payload["q"] = "";

      let myFilter = { account_type: "ma", id: { eq: audiences } };

      const newFilterStr = filterParser(myFilter);
      if (newFilterStr) {
        payload["filter"] = newFilterStr;
      }
      const { data } = await api.users.search(payload);

      setMaList(data ? data : []);
    }
  };

  const ListDateRecord = ({ label, date }) => (
    <SuiBox display="flex" pt={1} className="flex gap-1 items-start">
      <SuiTypography
        variant="body1"
        fontWeight="bold"
        textTransform="capitalize"
        color="text"
        sx={{ width: 140 }}
      >
        {label}
      </SuiTypography>
      <SuiBox>
        <SuiTypography variant="body1" textTransform="capitalize" color="secondary">
          {date ? moment(project.metadata.actived_at).format("MM/DD/yy") : "N/A"}
        </SuiTypography>
      </SuiBox>
    </SuiBox>
  );
  const responseMap = project?.response || {}; //{manufacturer_id:{date:xxx,message:xxx}}

  return (
    <div>
      <SuiBox sx={{ minHeight: "100%" }} ref={ref}>
        <div className="flex flex-wrap justify-between">
          <SuiBox key={"project-info"} display="flex" className="gap-1 items-center">
            <SuiTypography variant="h6" fontWeight="bold" textTransform="capitalize">
              Project Information
            </SuiTypography>
          </SuiBox>
        </div>
        <SuiBox key={"project schedule"}>
          <SuiBox>
            <SuiTypography
              variant="button"
              fontWeight="bold"
              textTransform="capitalize"
              sx={{ whiteSpace: "nowrap", minWidth: 150 }}
            >
              Schedule:
            </SuiTypography>
          </SuiBox>
          <SuiBox pl={2}>
            <ListDateRecord
              label="Desired Started Date:"
              date={project?.quote?.desired_start_date}
            />
            <ListDateRecord label="Desired End Date:" date={project?.quote?.desired_end_date} />
          </SuiBox>
        </SuiBox>

        <div>
          <SuiBox key={"budget"} display="flex" py={1} pr={2} className=" items-center gap-1">
            <SuiTypography
              variant="button"
              fontWeight="bold"
              textTransform="capitalize"
              sx={{ minWidth: 155 }}
            >
              Budget:
            </SuiTypography>
            {budgetMin != budgetMax ? (
              <SuiBox display="flex" alignItems="center">
                <NumberFormat
                  value={budgetMin}
                  displayType={"text"}
                  thousandSeparator={true}
                  prefix={"$"}
                  renderText={(value, props) => (
                    <SuiTypography variant="body1" fontWeight="regular" color="text" {...props}>
                      {value}
                    </SuiTypography>
                  )}
                />
                ~{" "}
                {budgetMax > 0 ? (
                  <NumberFormat
                    value={budgetMax}
                    displayType={"text"}
                    thousandSeparator={true}
                    prefix={"$"}
                    renderText={(value, props) => (
                      <SuiTypography variant="body1" fontWeight="regular" color="text" {...props}>
                        {value}
                      </SuiTypography>
                    )}
                  />
                ) : null}
              </SuiBox>
            ) : (
              <NumberFormat
                value={budgetMin}
                displayType={"text"}
                thousandSeparator={true}
                prefix={"$"}
                renderText={(value, props) => (
                  <SuiTypography variant="body1" fontWeight="regular" color="text" {...props}>
                    {value}
                  </SuiTypography>
                )}
              />
            )}
          </SuiBox>
        </div>
        <SuiBox key={"description"} display="flex" py={1} pr={2} className="gap-1 items-start">
          <SuiTypography
            variant="button"
            fontWeight="bold"
            textTransform="capitalize"
            sx={{ whiteSpace: "nowrap", minWidth: 155 }}
          >
            Project Description:
          </SuiTypography>

          <SuiTypography variant="body1" textTransform="none" color="text">
            {project.description ? (
              <ReadMore limit={2400} variant="body1" textTransform="none" color="text">
                {project.description}
              </ReadMore>
            ) : (
              "There is no description for this project."
            )}
          </SuiTypography>
        </SuiBox>
        <SuiBox my={1}>
          <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
            Service needed
          </SuiTypography>
          <SuiBox pl={2}>
            {renderServiceItems && renderServiceItems.length > 0 ? (
              renderServiceItems
            ) : (
              <SuiTypography variant="body1" textTransform="none" color="text">
                {"N/A"}
              </SuiTypography>
            )}
            {project?.quote?.services_description ? (
              <SuiBox
                key={"services_description"}
                display="flex"
                py={1}
                pr={2}
                gap={0.5}
                alignItems="top"
              >
                <SuiTypography
                  variant="button"
                  fontWeight="bold"
                  textTransform="capitalize"
                  color="text"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Special Instruction:
                </SuiTypography>

                <ReadMore limit={600} variant="body1" textTransform="none" color="text">
                  {project.quote.services_description}
                </ReadMore>
              </SuiBox>
            ) : null}
          </SuiBox>
        </SuiBox>
        {othersFiles && othersFiles.length > 0 ? (
          <SuiBox key={"reference_files"} display="flex" py={1} alignItems="top">
            <SuiTypography
              variant="button"
              fontWeight="bold"
              textTransform="capitalize"
              sx={{ whiteSpace: "nowrap", mt: 1, minWidth: 155 }}
            >
              Reference files:
            </SuiTypography>
            <FilePreviewCard files={othersFiles} handleDelete={false} />
          </SuiBox>
        ) : null}

        {maList && maList?.hits && maList?.hits.length > 0 ? (
          <SuiBox key={"sent requests"} py={1} pr={2} className="gap-1 items-start">
            <SuiTypography
              variant="button"
              fontWeight="bold"
              textTransform="capitalize"
              sx={{ whiteSpace: "nowrap" }}
            >
              Request Sent To:
            </SuiTypography>

            <SuiBox display="flex" flexWrap="wrap" gap={4} pl={2}>
              {maList.hits.map((user, index) =>
                user?.stores && user.stores.length > 0 ? (
                  <SuiBox
                    key={index}
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                    gap={1}
                    mb={0.5}
                    minWidth={200}
                  >
                    <SuiBox component={Link} to={`/wall/portfolio/${user.id}`}>
                      <Tooltip position="top" title={user.stores[0]?.name}>
                        <SuiAvatar
                          src={user.stores[0]?.metadata?.avatar}
                          alt="profile-image"
                          variant="circle"
                          size="xs"
                          shadow="sm"
                        />
                      </Tooltip>
                    </SuiBox>
                    <SuiBox component={Link} to={`/wall/portfolio/${user.id}`}>
                      <SuiTypography
                        variant="button"
                        fontWeight="regular"
                        textTransform="capitalize"
                      >
                        {user.stores[0]?.name}
                      </SuiTypography>
                    </SuiBox>
                    {showResponseStatus && responseMap[user.id] ? (
                      <Tooltip position="top" title={"This manufacturer already responded to you!"}>
                        <MarkChatReadIcon fontSize="small" color="success" />
                      </Tooltip>
                    ) : null}
                  </SuiBox>
                ) : null
              )}
            </SuiBox>
          </SuiBox>
        ) : null}
      </SuiBox>
    </div>
  );
});

export default ProjectInfo;
